import type { ExecutionStatus } from "@deft/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api";

export interface BackgroundTask {
  id: string;
  instruction: string;
  status: ExecutionStatus;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export function useBackgroundTasks(conversationId: string) {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/tasks?conversationId=${conversationId}`);
      if (res.ok) {
        setTasks((await res.json()) as BackgroundTask[]);
      }
    } catch {
      // Non-critical
    }
  }, [conversationId]);

  const activeTasks = tasks.filter(
    (t) => t.status === "pending" || t.status === "running",
  );
  const hasActiveTasks = activeTasks.length > 0;

  // Fetch once on mount
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Poll only while there are active tasks
  useEffect(() => {
    if (hasActiveTasks) {
      intervalRef.current = setInterval(fetchTasks, 3000);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [hasActiveTasks, fetchTasks]);

  return { tasks, activeTasks, hasActiveTasks, refresh: fetchTasks };
}
